import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  where, 
  limit, 
  orderBy, 
  addDoc, 
  setDoc,
  deleteDoc,
  serverTimestamp 
} from "firebase/firestore";
import { db } from "config/firebase";

// Ultimate recursive sanitization for Firestore
export const sanitizeForFirestore = (data) => {
  if (data === undefined) return null;
  if (data === null) return null;
  
  // Handle primitives
  if (typeof data !== 'object') return data;
  
  // Preserve Firestore-compatible special objects and avoid corrupting them
  if (data instanceof Date) return data;
  
  // CRITICAL: Check for Firestore internal objects (FieldValue, Timestamp, etc)
  // These usually have a method like toJSON or a specific internal property
  if (data && typeof data.constructor === 'function') {
    const cName = data.constructor.name;
    if (cName === 'FieldValue' || cName === 'Timestamp' || cName === 'GeoPoint' || cName === 'DocumentReference') {
      return data; 
    }
  }

  // Handle Arrays
  if (Array.isArray(data)) {
    return data.map(item => sanitizeForFirestore(item));
  }
  
  // Handle Objects - ensure it's a plain data object
  const clean = {};
  Object.keys(data).forEach(key => {
    const val = data[key];
    if (val !== undefined && typeof val !== 'function') {
      clean[key] = sanitizeForFirestore(val);
    }
  });
  return clean;
};

// Helper function to handle village data
export const getVillageDoc = async (slug) => {
  const q = query(collection(db, "villages"), where("slug", "==", slug));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;
  return querySnapshot.docs[0];
};

export async function getSejarah(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    // Check if it's a document in profile (new style)
    const historyDoc = await getDoc(doc(db, `villages/${villageDoc.id}/profile`, "sejarah"));
    if (historyDoc.exists()) {
      return { error: false, data: { data: [historyDoc.data()] } };
    }

    // Fallback: check history collection (CMS list style)
    const qSnapshot = await getDocs(query(collection(db, `villages/${villageDoc.id}/history`), limit(1)));
    const items = qSnapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        deskripsi: d.content || d.deskripsi || "",
        url_gambar: d.imageUrl || d.url_gambar || ""
      };
    });
    
    return { error: false, data: { data: items } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getProfile(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const profileDoc = await getDoc(doc(db, `villages/${villageDoc.id}/profile`, "general"));
    let profileData = profileDoc.exists() ? profileDoc.data() : {};
    
    // Ensure structure for components
    if (!profileData.ProfilDesa) profileData.ProfilDesa = { nama: villageDoc.data().name || slug.replace(/-/g, ' ').toUpperCase() };
    if (!profileData.KepalaDesa) profileData.KepalaDesa = {};
    
    // Fetch Latest News for Slider (News.js component expects profile.Berita)
    const newsQ = query(collection(db, `villages/${villageDoc.id}/news`), orderBy("createdAt", "desc"), limit(6));
    const newsSnap = await getDocs(newsQ);
    profileData.Berita = newsSnap.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        uuid: doc.id,
        judul: d.title || d.judul || "Tanpa Judul",
        url_gambar: d.imageUrl || d.url_gambar || "",
        list_kategori_berita_obj: d.categories && Array.isArray(d.categories) 
          ? d.categories.map(cat => (typeof cat === 'string' ? { kategori_obj: { nama: cat } } : cat))
          : [{ kategori_obj: { nama: "Umum" } }]
      };
    });

    // Fetch Latest Documents (DocumentPage snippet)
    const docsQ = query(collection(db, `villages/${villageDoc.id}/documents`), orderBy("createdAt", "desc"), limit(3));
    const docsSnap = await getDocs(docsQ);
    profileData.Dokumen = docsSnap.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        uuid: doc.id,
        nama: d.title || d.judul || d.nama || "Tanpa Judul",
        url_gambar: d.imageUrl || d.url_gambar || "",
        create_date: d.date || d.createdAt?.toDate()?.toISOString() || new Date().toISOString(),
        list_kategori_dokumen_obj: d.categories && Array.isArray(d.categories)
          ? d.categories.map(cat => (typeof cat === 'string' ? { kategori_obj: { nama: cat } } : cat))
          : [{ kategori_obj: { nama: "Umum" } }]
      };
    });

    // Fetch Latest Activities (DocumentPage slider)
    const activitiesQ = query(collection(db, `villages/${villageDoc.id}/activities`), orderBy("createdAt", "desc"), limit(5));
    const activitiesSnap = await getDocs(activitiesQ);
    profileData.Kegiatan = activitiesSnap.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        uuid: doc.id,
        nama: d.title || d.nama || "Tanpa Judul",
        url_gambar: d.imageUrl || d.url_gambar || "",
        list_kategori_kegiatan_obj: d.categories && Array.isArray(d.categories)
          ? d.categories.map(cat => (typeof cat === 'string' ? { kategori_obj: { nama: cat } } : cat))
          : [{ kategori_obj: { nama: "Umum" } }]
      };
    });

    // Fetch Latest Achievements (Capaian.js component)
    const achievementsQ = query(collection(db, `villages/${villageDoc.id}/achievements`), orderBy("createdAt", "desc"), limit(6));
    const achievementsSnap = await getDocs(achievementsQ);
    profileData.CapaianGampong = achievementsSnap.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        uuid: doc.id,
        nama: d.title || d.judul || "Tanpa Judul",
        url_gambar: d.imageUrl || d.url_gambar || "",
        list_kategori_capaian_obj: d.categories && Array.isArray(d.categories)
          ? d.categories.map(cat => (typeof cat === 'string' ? { kategori_obj: { nama: cat } } : cat))
          : [{ kategori_obj: { nama: "Gallery" } }]
      };
    });

    return { error: false, data: { data: profileData } };
  } catch (error) {
    console.error("error getProfile", error);
    return { error: true, message: error.message };
  }
}
export async function updateProfile(slug, data) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    // 1. Force generic object via JSON to strip any hidden proxies or prototypes
    const raw = JSON.parse(JSON.stringify(data || {}));
    
    const p = raw.ProfilDesa || {};
    const k = raw.KepalaDesa || {};
    
    // 2. Manual, Explicit Reconstruction 
    // We avoid recursive sanitization on the final object to prevent issues with field values
    const finalData = {
      ProfilDesa: {
        nama: String(p.nama || ""),
        sambutan: String(p.sambutan || ""),
        url_gambar: String(p.url_gambar || ""),
        luas_wilayah: String(p.luas_wilayah || ""),
        luas_area_pertanian: String(p.luas_area_pertanian || ""),
        luas_permukiman: String(p.luas_permukiman || ""),
        latitude: String(p.latitude || ""),
        longitude: String(p.longitude || "")
      },
      KepalaDesa: {
        nama: String(k.nama || ""),
        jabatan_obj: { 
          nama: String(k.jabatan_obj?.nama || "Keuchik")
        },
        telepon: String(k.telepon || ""),
        email: String(k.email || ""),
        url_gambar: String(k.url_gambar || "")
      },
      JumlahPenduduk: String(raw.JumlahPenduduk || "0"),
      updatedAt: serverTimestamp() // Added as a FieldValue
    };

    console.log("DEBUG: Final payload keys:", Object.keys(finalData));
    console.log("DEBUG: Image sizes:", {
      hero: finalData.ProfilDesa.url_gambar.length,
      kepala: finalData.KepalaDesa.url_gambar.length
    });

    await setDoc(doc(db, `villages/${villageDoc.id}/profile`, "general"), finalData, { merge: true });
    
    return { error: false, message: "Profil berhasil diperbarui" };
  } catch (error) {
    console.error("CRITICAL ERROR: updateProfile failed", error);
    // Be more descriptive about the error
    let message = error.message;
    if (message.includes("blocked by client")) {
      message = "Gagal mengirim data. Matikan AdBlocker/uBlock dan coba lagi (gambar terlalu besar).";
    }
    return { error: true, message: "Gagal menyimpan: " + message };
  }
}

export async function updateProfileDoc(slug, docId, data) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    // Sanitize data recursively
    const cleanData = sanitizeForFirestore(data);

    await setDoc(doc(db, `villages/${villageDoc.id}/profile`, docId), {
      ...cleanData,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return { error: false, message: "Data berhasil diperbarui" };
  } catch (error) {
    console.error("error updateProfileDoc", error);
    return { error: true, message: error.message };
  }
}

export async function updateSdmData(slug, data) {
  return updateProfileDoc(slug, "sdm", data);
}

export async function updateGeographyData(slug, coll, id, data) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };

    const cleanData = sanitizeForFirestore(data);
    await setDoc(doc(db, `villages/${villageDoc.id}/${coll}`, id), {
      ...cleanData,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { error: false, message: "Data berhasil diperbarui" };
  } catch (error) {
    console.error(`error updateGeographyData ${coll}`, error);
    return { error: true, message: error.message };
  }
}

export async function addGeographyData(slug, coll, data) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };

    const cleanData = sanitizeForFirestore(data);
    const docRef = await addDoc(collection(db, `villages/${villageDoc.id}/${coll}`), {
      ...cleanData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { error: false, id: docRef.id };
  } catch (error) {
    console.error(`error addGeographyData ${coll}`, error);
    return { error: true, message: error.message };
  }
}

export async function deleteGeographyData(slug, coll, id) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };

    await deleteDoc(doc(db, `villages/${villageDoc.id}/${coll}`, id));
    return { error: false };
  } catch (error) {
    console.error(`error deleteGeographyData ${coll}`, error);
    return { error: true, message: error.message };
  }
}

export async function getDocumentById(uuid, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const docRef = doc(db, `villages/${villageDoc.id}/documents`, uuid);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return { error: true, message: "Document not found" };
    
    const d = docSnap.data();
    return { 
      error: false, 
      data: { 
        data: { 
          id: docSnap.id, 
          uuid: docSnap.id,
          judul: d.title || d.judul || "Tanpa Judul",
          nama: d.title || d.nama || "Tanpa Judul",
          url_gambar: d.imageUrl || d.url_gambar || "",
          create_date: d.date || d.createdAt?.toDate()?.toLocaleDateString() || "",
          isi: d.content || d.isi || "",
          fileUrl: d.fileUrl || ""
        } 
      } 
    };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getKategoriDoc(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const querySnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/document_categories`));
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getKategoriCapaian(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const querySnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/achievement_categories`));
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getDocuments(lim, off, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const q = query(
      collection(db, `villages/${villageDoc.id}/documents`), 
      orderBy("createdAt", "desc"),
      limit(lim)
    );
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        uuid: doc.id,
        judul: d.title || d.judul || d.nama || "Dokumen Tanpa Judul",
        nama: d.title || d.nama || d.judul || "Dokumen Tanpa Judul",
        url_gambar: d.imageUrl || d.url_gambar || "",
        create_date: d.date || d.createdAt?.toDate()?.toLocaleDateString() || "",
        fileUrl: d.fileUrl || ""
      };
    });
    return { error: false, data: { data, jumlahData: data.length > 0 ? 100 : 0 } };
  } catch (error) {
    console.error("error getDocuments", error);
    return { error: true, message: error.message };
  }
}

export async function searchBerita(lim, off, keyword, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const q = query(
      collection(db, `villages/${villageDoc.id}/news`), 
      where("title", ">=", keyword),
      where("title", "<=", keyword + "\uf8ff"),
      limit(lim)
    );
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function searchDoc(lim, off, keyword, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const q = query(
      collection(db, `villages/${villageDoc.id}/documents`), 
      where("title", ">=", keyword),
      where("title", "<=", keyword + "\uf8ff"),
      limit(lim)
    );
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getDocByKategori(lim, off, kategoriId, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const q = query(
      collection(db, `villages/${villageDoc.id}/documents`),
      where("categoryId", "==", kategoriId),
      limit(lim)
    );
    const qSnapshot = await getDocs(q);
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getKegiatan(lim, off, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    // Sort by createdAt or date
    const q = query(
      collection(db, `villages/${villageDoc.id}/activities`),
      orderBy("createdAt", "desc"),
      limit(lim)
    );
    
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        uuid: doc.id, // For legacy uuid support
        nama: d.title || d.nama || "Tanpa Judul",
        url_gambar: d.imageUrl || d.url_gambar || "",
        create_date: d.date || d.createdAt?.toDate()?.toLocaleDateString() || "",
        content: d.content || "",
        list_kategori_kegiatan_obj: d.categories || [] // Mock category if empty
      };
    });
    
    return { error: false, data: { data, jumlahData: data.length > 0 ? 100 : 0 } }; // Mocking total for now
  } catch (error) {
    console.error("error getKegiatan", error);
    return { error: true, message: error.message };
  }
}

export async function getKegiatanById(uuid, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const docSnap = await getDoc(doc(db, `villages/${villageDoc.id}/activities`, uuid));
    if (!docSnap.exists()) return { error: true, message: "Activity not found" };
    
    const d = docSnap.data();
    return { 
      error: false, 
      data: { 
        data: {
          id: docSnap.id,
          uuid: docSnap.id,
          nama: d.title || d.nama || "Tanpa Judul",
          url_gambar: d.imageUrl || d.url_gambar || "",
          create_date: d.date || d.createdAt?.toDate()?.toLocaleDateString() || "",
          isi: d.content || d.isi || "",
          fileUrl: d.fileUrl || "",
          list_kategori_kegiatan_obj: d.categories || []
        }
      } 
    };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getKategoriKegiatan(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const querySnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/activity_categories`));
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getKegByKategori(lim, off, kategoriId, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const q = query(
      collection(db, `villages/${villageDoc.id}/activities`),
      where("categoryId", "==", kategoriId),
      orderBy("date", "desc"),
      limit(lim)
    );
    const qSnapshot = await getDocs(q);
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function searchKegiatan(lim, off, keyword, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const q = query(
      collection(db, `villages/${villageDoc.id}/activities`), 
      where("title", ">=", keyword),
      where("title", "<=", keyword + "\uf8ff"),
      limit(lim)
    );
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getBerita(lim, off, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const q = query(
      collection(db, `villages/${villageDoc.id}/news`),
      orderBy("createdAt", "desc"),
      limit(lim)
    );
    
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        uuid: doc.id,
        judul: d.title || d.judul || "Tanpa Judul",
        url_gambar: d.imageUrl || d.url_gambar || "",
        create_date: d.date || d.createdAt?.toDate()?.toLocaleDateString() || "",
        content: d.content || "",
        list_kategori_berita_obj: d.categories || []
      };
    });
    
    return { error: false, data: { data, jumlahData: data.length > 0 ? 100 : 0 } };
  } catch (error) {
    console.error("error getBerita", error);
    return { error: true, message: error.message };
  }
}

export async function getBeritaById(uuid, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const docSnap = await getDoc(doc(db, `villages/${villageDoc.id}/news`, uuid));
    if (!docSnap.exists()) return { error: true, message: "News not found" };
    
    const d = docSnap.data();
    return { 
      error: false, 
      data: { 
        data: {
          id: docSnap.id,
          uuid: docSnap.id,
          judul: d.title || d.judul || "Tanpa Judul",
          url_gambar: d.imageUrl || d.url_gambar || "",
          create_date: d.date || d.createdAt?.toDate()?.toLocaleDateString() || "",
          isi: d.content || d.isi || "",
          fileUrl: d.fileUrl || "",
          list_kategori_berita_obj: d.categories || [{ kategori_obj: { nama: "Umum", uuid: "umum" } }]
        }
      } 
    };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getKategoriBerita(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const querySnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/news_categories`));
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getBeritaPopuler(lim, off, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const q = query(
      collection(db, `villages/${villageDoc.id}/news`),
      orderBy("views", "desc"),
      limit(lim)
    );
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getPemerintahan(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const docSnap = await getDoc(doc(db, `villages/${villageDoc.id}/profile`, "government"));
    if (!docSnap.exists()) return { error: true, message: "Governance data not found" };
    
    return { error: false, data: { data: docSnap.data() } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getSdm(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const docSnap = await getDoc(doc(db, `villages/${villageDoc.id}/profile`, "sdm"));
    if (!docSnap.exists()) return { error: true, message: "SDM data not found" };
    
    return { error: false, data: { data: docSnap.data() } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getLandingPage() {
  try {
    const qSnapshot = await getDocs(collection(db, "landing_page_data"));
    return { error: false, data: { data: qSnapshot.docs.map(doc => doc.data()) } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getDesaLandingPage() {
  try {
    const qSnapshot = await getDocs(collection(db, "villages"));
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function searchDesaLandingPage(keyword) {
  try {
    const q = query(
      collection(db, "villages"),
      where("name", ">=", keyword.toUpperCase()),
      where("name", "<=", keyword.toUpperCase() + "\uf8ff")
    );
    const qSnapshot = await getDocs(q);
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function filterDesaLandingPage(prov, kab, kec) {
  try {
    let q = query(collection(db, "villages"));
    if (prov !== 'null') q = query(q, where("provinsi_id", "==", prov));
    if (kab !== 'null') q = query(q, where("kabupaten_id", "==", kab));
    if (kec !== 'null') q = query(q, where("kecamatan_id", "==", kec));
    
    const qSnapshot = await getDocs(q);
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function sendLaporan(data, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    await addDoc(collection(db, `villages/${villageDoc.id}/reports`), {
      ...data,
      createdAt: serverTimestamp()
    });
    
    return { error: false, message: "Laporan berhasil dikirim" };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function registerDesa(data) {
  try {
    await addDoc(collection(db, "registration_requests"), {
      ...data,
      status: "pending",
      createdAt: serverTimestamp()
    });
    
    return { error: false, message: "Pendaftaran desa berhasil dikirim" };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getBeritaByKategori(lim, off, kategoriId, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const q = query(
      collection(db, `villages/${villageDoc.id}/news`),
      where("categoryId", "==", kategoriId),
      orderBy("date", "desc"),
      limit(lim)
    );
    const qSnapshot = await getDocs(q);
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getVisiMisi(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const docSnap = await getDoc(doc(db, `villages/${villageDoc.id}/profile`, "visimisi"));
    if (docSnap.exists()) {
      return { error: false, data: { data: [docSnap.data()] } };
    }

    // Fallback: check visimisi collection
    const qSnapshot = await getDocs(query(collection(db, `villages/${villageDoc.id}/visimisi`), limit(1)));
    const items = qSnapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        visi: d.visi || d.title || "",
        misi: d.misi || d.content || "",
        url_gambar: d.imageUrl || d.url_gambar || ""
      };
    });
    
    return { error: false, data: { data: items } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getVisi(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    const docSnap = await getDoc(doc(db, `villages/${villageDoc.id}/profile`, "visimisi"));
    return { error: false, data: { data: docSnap.exists() ? docSnap.data().visi : "" } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getMisi(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    const docSnap = await getDoc(doc(db, `villages/${villageDoc.id}/profile`, "visimisi"));
    return { error: false, data: { data: docSnap.exists() ? docSnap.data().misi : [] } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getFasilitasGampong(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const qSnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/facilities`));
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getQanun(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const qSnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/qanun`));
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getBatasGampong(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const qSnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/batas_gampong`));
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getDusun(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const qSnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/dusun`));
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getPendudukDusun(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const qSnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/penduduk_dusun`));
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getOrbitasi(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const qSnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/orbitasi`));
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getGallery(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const q = query(
      collection(db, `villages/${villageDoc.id}/gallery`),
      orderBy("createdAt", "desc")
    );
    const qSnapshot = await getDocs(q);
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

// end daerah

export async function getPencapaian(lim, off, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const q = query(
      collection(db, `villages/${villageDoc.id}/achievements`),
      orderBy("createdAt", "desc"),
      limit(lim)
    );
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        uuid: doc.id,
        judul: d.title || d.judul || "Pencapaian Tanpa Judul",
        url_gambar: d.imageUrl || d.url_gambar || "",
        create_date: d.date || d.createdAt?.toDate()?.toLocaleDateString() || "",
        isi: d.content || d.isi || ""
      };
    });
    return { error: false, data: { data, jumlahData: data.length > 0 ? 100 : 0 } };
  } catch (error) {
    console.error("error getPencapaian", error);
    return { error: true, message: error.message };
  }
}

export async function searchCapaian(lim, off, keyword, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const q = query(
      collection(db, `villages/${villageDoc.id}/achievements`), 
      where("title", ">=", keyword),
      where("title", "<=", keyword + "\uf8ff"),
      limit(lim)
    );
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getCapaianByKategori(lim, off, kategoriId, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const q = query(
      collection(db, `villages/${villageDoc.id}/achievements`),
      where("categoryId", "==", kategoriId),
      limit(lim)
    );
    const qSnapshot = await getDocs(q);
    const data = qSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { error: false, data: { data } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getPencapaianById(uuid, slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    const docSnap = await getDoc(doc(db, `villages/${villageDoc.id}/achievements`, uuid));
    return { error: false, data: { data: docSnap.exists() ? docSnap.data() : {} } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getSdg(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const docSnap = await getDoc(doc(db, `villages/${villageDoc.id}/profile`, "sdg"));
    if (!docSnap.exists()) return { error: true, message: "SDG data not found" };
    
    return { error: false, data: { data: docSnap.data() } };
  } catch (error) {
    return { error: true, message: error.message };
  }
}

export async function getPerangkatDesa(slug) {
  try {
    const villageDoc = await getVillageDoc(slug);
    if (!villageDoc) return { error: true, message: "Village not found" };
    
    const querySnapshot = await getDocs(collection(db, `villages/${villageDoc.id}/staff`));
    const data = querySnapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        nama: d.title || d.nama || "Tanpa Nama",
        url_gambar: d.imageUrl || d.url_gambar || "",
        telepon: d.telepon || "",
        email: d.email || "",
        deskripsi: d.content || d.deskripsi || "",
        jabatan_obj: { nama: d.jabatan || (d.jabatan_obj?.nama) || "Staff" }
      };
    });
    
    return { error: false, data: { data } };
  } catch (error) {
    console.error("error getPerangkatDesa", error);
    return { error: true, message: error.message };
  }
}

